import { readPlayerCharacterField } from '../janus-engine/display/player-character-field';
import { renderClassLevelIcons } from '../janus-engine/port/k5-render/class-level-icons';
import { plTotal, plPretty, plExact } from '../shared/array-types';
import { makeNumberPretty } from '../object-card/helpers';
import type { TableRow, TableCell, CellSegment } from './types';
import { decodeHtmlEntitiesForPlainText } from '../shared/decode-html-entities';
import { compilePictureRow } from '../janus-engine/compile/picture-row-construction';
import type { ThumbnailData, PictureEntry } from '../thumbnail/core';
import { hydrateDefinitionFamilyCells } from './definition-family-cells';
import { definitionPlCell } from './definition-cells';
import { phpJsonInt, phpJsonString } from '../janus-engine/compile/php-json-values';
import { checkUuid } from '../janus-engine/pl/contract';

type Row = Record<string, unknown>;
const record = (v: unknown): Row => v && typeof v === 'object' && !Array.isArray(v) ? v as Row : {};
const scalar = (v: unknown): unknown => Array.isArray(v) ? v[0] : v;
const uuid = (v: unknown): string => typeof v === 'string' ? v : String(ftItemsCol(v, 'item_id') ?? record(v).item_id ?? '');

export interface CreatureCatalogViewer { id: string; isAdmin: boolean; }
type CreatureCatalogRow = Omit<TableRow, 'cells'> & {
    cells?: TableRow['cells']; client_creature?: Row; client_source?: Row;
};
const creatureCatalogViewer = (): CreatureCatalogViewer => {
    const user = typeof window === 'undefined' ? undefined
        : (window as unknown as { FateTide?: { user?: { id?: string; primaryRole?: string } } }).FateTide?.user;
    return { id: String(user?.id ?? ''), isAdmin: ['admin', 'super_admin'].includes(user?.primaryRole ?? '') };
};

/** The former PHP rule uses an integer privacy cast and an exact creator id
 * comparison. Employees have no admin override. Keep saved media untouched. */
export function creaturePictureVisible(picture: unknown, viewer: CreatureCatalogViewer): boolean {
    return phpJsonInt(pictureField0(picture, 'picture_private') ?? 0) === 0 || viewer.isAdmin
        || (viewer.id !== '' && phpJsonString(pictureField0(picture, 'picture_created_by_id')) === viewer.id);
}

// ft_catalog_text decoded a creature name once (taxonomy/source labels below
// already use the shared bounded decoder). Preserve that stored-name behavior.
const creatureName = (creature: Row): string => {
    const name = String(ftItemsCol(creature, 'item_name') ?? '');
    if (!name.includes('&')) return name;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = name;
    return textarea.value;
};

/** Shared creature list projection. All cell strings are data segments; the
 * table's existing renderer creates text nodes and escapes labels/tooltips. */
export function hydrateCreatureCells(rows: CreatureCatalogRow[], catalog: Row, sizes: Row[], viewer = creatureCatalogViewer()): void {
    if (hydrateDefinitionFamilyCells(rows as TableRow[], catalog)) return;
    for (const row of rows) {
        const source = row.client_creature;
        if (!source) continue;
        const creature = { ...source, catalog } as Row & { computed?: unknown };
        // Accept both the old PHP cell envelope and raw saved rows during the
        // staggered endpoint/bundle rollout. Every creature cell is rebuilt here.
        const legacyThumbnail = row.cells?.picture?.segments?.find(segment => segment.kind === 'thumbnail');
        const rawPictures = source.pictures ?? (legacyThumbnail?.kind === 'thumbnail' ? legacyThumbnail.pictures : {});
        const pictures: NonNullable<ThumbnailData['pictures']> = {};
        const buckets = Array.isArray(rawPictures) ? { item: rawPictures } : record(rawPictures);
        for (const [bucket, entries] of Object.entries(buckets)) for (const raw of Array.isArray(entries) ? entries : []) {
            if (!creaturePictureVisible(raw, viewer)) continue;
            const type = String(pictureField0(raw, 'picture_attached_item_type') ?? bucket);
            const picture = pictureField0(raw, 'picture_url') ? raw as PictureEntry
                : compilePictureRow(raw, { cdnUrl: 'https://cdn.fatetide.com/' }) as PictureEntry;
            (pictures[type] ??= []).push(picture);
        }
        const name = creatureName(creature);
        const cells: Record<string, TableCell> = row.cells = {
            name: { segments: [{ kind: 'text', value: name, link: `/item.php?id=${encodeURIComponent(row.item_id)}` }], sort: name, text: name.toLowerCase() },
            picture: { segments: [{ kind: 'thumbnail', item_id: row.item_id, name, pictures, w: 90, h: 90, link: true }], sort: name, text: '' },
        };
        const textCell = (key: string, value: unknown, segments?: CellSegment[]): void => {
            const text = String(scalar(value) ?? '');
            cells[key] = { sort: text !== '' && Number.isFinite(Number(text)) ? Number(text) : text,
                text: text.toLowerCase(), segments: segments ?? [{ kind: 'text', value: text }] };
        };
        const refs = (layer: string): string[] => ftLayerEntries(creature, layer).map(uuid).filter(Boolean);
        for (const key of ['size', 'creature_type', 'creature_sub_type', 'creature_role']) {
            const ids = refs(key), id = ids[0] ?? uuid(ftLayerValue(creature, key));
            const def = catalog[id] ?? catalog[id.toUpperCase()];
            const name = decodeHtmlEntitiesForPlainText(String(ftItemsCol(def, 'item_name') ?? ''));
            const segments: CellSegment[] = id ? [{ kind: 'icon', id, item_type: key === 'size' ? 'size_type' : key, title: name || false, link: `/item.php?id=${encodeURIComponent(id)}` }] : [];
            if (key === 'creature_sub_type' && name) segments.push({ kind: 'text', value: name });
            textCell(key, name, segments);
            if (key === 'size') cells[key]!.sort = Number(sizes.find(size => String(size.item_id) === id)?.weight_multiplier ?? 0);
        }
        textCell('cr', ftLayerValue(creature, 'cr'));
        const weight = ftLayerValue(creature, 'weight');
        textCell('weight', weight, weight == null ? [] : [{ kind: 'text', value: makeNumberPretty(weight) }]);
        const cost = ftLayerValue(creature, 'cost_gp');
        textCell('cost_gp', cost, cost == null ? [] : Number(cost) === 0 ? [{ kind: 'icon', id: 'priceless' }] : [{ kind: 'text', value: makeNumberPretty(cost) }]);
        const bit = ftLayerValue(creature, 'monster_bit') != null;
        textCell('monster_bit', bit ? 1 : 0, bit ? [{ kind: 'icon', id: 'monster_bit' }] : []);
        // PL stays pending until the browser has a price; the existing table
        // pricer fills it from the definition, with no PHP pricing fallback.
        const pl = plTotal(creature);
        cells.power_level = checkUuid(row.item_id) ? definitionPlCell(undefined, row.item_id) : { segments: [], sort: '', text: '' };
        if (pl != null) textCell('power_level', pl, [{ kind: 'badge', value: plPretty(pl), variant: 'white', cls: 'text-dark', tip: 'Power Level: ' + plExact(pl) }]);
        const sourceRow = row.client_source;
        textCell('source', '', []);
        if (sourceRow) {
            const rawSource = record(sourceRow.sources ?? sourceRow);
            const sourceId = String(ftItemsCol(rawSource, 'source_id') ?? '');
            const label = decodeHtmlEntitiesForPlainText(String(ftItemsCol(rawSource, 'source_abbreviation') ?? ftItemsCol(rawSource, 'source_name') ?? ''));
            textCell('source', label, [{ kind: 'text', value: label, link: `/source.php?source_id=${encodeURIComponent(sourceId)}` }]);
        }
        row.filters = { ...row.filters, sources: String(ftItemsCol(record(sourceRow?.sources ?? sourceRow), 'source_id') ?? ftItemsCol(creature, 'source_id') ?? ''), pl: pl == null ? '' : String(pl),
            ...Object.fromEntries(['creature_type', 'creature_sub_type', 'creature_template', 'size', 'damage_type_resistance',
                'damage_type_immunity', 'damage_type_vulnerability', 'status_effect_permanent', 'creature_environment', 'lair', 'regional_effect'].map(key => [key, refs(key)])) };
    }
}

export function rosterComputedCell(computed: unknown, key: string): string {
    if (!computed || typeof computed !== 'object') return '';
    return String(ftLayerValue({ computed }, key) ?? '');
}

export function rosterClassIcons(row: Row): string {
    const field = (key: string): unknown => readPlayerCharacterField(row, key);
    const id = (key: string): string => { const value = field(key); return typeof value === 'string' ? value : String(ftItemsCol(value, 'item_id') ?? ''); };
    return renderClassLevelIcons({ field, id, escape: escapeHtml,
        attrs: item => `data-ft-ability-row-action="open-info" data-ft-item-id="${escapeHtml(item)}" data-ft-info-item-id="${escapeHtml(item)}"`,
        icon: (item, size) => iconMap(item, size, size) }, 40);
}

/** Raw saved picture rows use the existing browser projection, not a PHP compile. */
export function rosterThumbnailData(input: Row, canSeePrivate: boolean): ThumbnailData {
    const pictures: PictureEntry[] = [];
    const number = (row: unknown, key: string): number => Number(pictureField0(row, key) ?? 0);
    // Match the media query order, including the tie-break for equal numbers.
    const rows = Object.values(input.pictures ?? {}).sort((a, b) => number(b, 'primary_picture') - number(a, 'primary_picture')
        || number(b, 'picture_thumbnail') - number(a, 'picture_thumbnail') || number(a, 'picture_number') - number(b, 'picture_number')
        || number(a, 'picture_i') - number(b, 'picture_i'));
    for (const raw of rows) {
        if (number(raw, 'picture_private') !== 0 && !canSeePrivate) continue;
        const picture = compilePictureRow(raw, { cdnUrl: String(input.cdn_url ?? 'https://cdn.fatetide.com/') }) as PictureEntry;
        if (pictureField0(picture, 'picture_attached_item_type') === 'player_character_picture') pictures.push(picture);
    }
    return { item_id: String(input.item_id ?? ''), name: String(input.name ?? ''), pictures: { player_character_picture: pictures },
        size: typeof input.size === 'string' ? input.size : undefined, w: 90, h: 90, link: true };
}

export function rosterThumbnailHtml(input: Row | undefined, canSeePrivate: boolean): string {
    if (!input) return '<span class="text-danger" data-ft-display-refusal>unported:roster:thumbnail-inputs-not-held</span>';
    const json = JSON.stringify(rosterThumbnailData(input, canSeePrivate)).replace(/</g, '\\u003c');
    return `<span class="ft-thumb-mount"><script type="application/json" class="ft-thumb-blob">${json}</script></span>`;
}
